/**
 * Feedback Trigger Service
 * Smart trigger logic for showing feedback modals
 * - End-of-day time-based triggers
 * - Event count-based triggers
 * - Random sampling
 * - Prevents over-prompting
 * - LocalStorage persistence
 * - PostHog integration for analytics
 */

class FeedbackTriggerService {
  constructor() {
    this.STORAGE_KEY = 'vcx_feedback_tracker';
    this.SESSION_KEY = 'vcx_session_data';

    // Configuration
    this.config = {
      // Time-based triggers
      endOfDayStartHour: 16, // 4 PM
      endOfDayEndHour: 18,   // 6 PM
      endOfDayChance: 0.3,   // 30% chance during window

      // Event count triggers
      eventsPerFeedback: 25, // Show after every N events

      // Frequency limits
      maxFeedbackPerDay: 1,  // Maximum 1 feedback request per day
      minHoursBetweenFeedback: 24, // Minimum hours between requests

      // Random sampling
      randomChance: 0.05, // 5% chance on any event (outside special triggers)
    };
  }

  /**
   * Check if feedback modal should be shown
   * @param {object} posthogClient - Optional PostHog client for tracking
   * @returns {boolean}
   */
  shouldShowFeedbackModal(posthogClient = null) {
    const tracker = this.getTracker();
    const now = new Date();

    // 1. Check if already shown today
    const todayString = this.getDateString(now);
    if (tracker.lastShownDate === todayString) {
      this.trackDecision(posthogClient, 'already_shown_today', false);
      return false;
    }

    // 2. Check if minimum time has passed since last feedback
    if (tracker.lastShownTimestamp) {
      const hoursSinceLastFeedback = (Date.now() - tracker.lastShownTimestamp) / (1000 * 60 * 60);
      if (hoursSinceLastFeedback < this.config.minHoursBetweenFeedback) {
        this.trackDecision(posthogClient, 'too_soon_since_last', false);
        return false;
      }
    }

    // 3. End-of-day trigger (high priority)
    const hour = now.getHours();
    if (hour >= this.config.endOfDayStartHour && hour < this.config.endOfDayEndHour) {
      const shouldShow = Math.random() < this.config.endOfDayChance;
      if (shouldShow) {
        this.trackDecision(posthogClient, 'end_of_day_trigger', true);
        return true;
      }
    }

    // 4. Event count trigger (medium priority)
    const sessionData = this.getSessionData();
    if (sessionData.eventsReviewed > 0 &&
        sessionData.eventsReviewed % this.config.eventsPerFeedback === 0) {
      this.trackDecision(posthogClient, 'event_count_trigger', true, {
        eventsReviewed: sessionData.eventsReviewed
      });
      return true;
    }

    // 5. Random sampling (low priority)
    const shouldShowRandom = Math.random() < this.config.randomChance;
    if (shouldShowRandom) {
      this.trackDecision(posthogClient, 'random_sampling', true);
      return true;
    }

    this.trackDecision(posthogClient, 'no_trigger_met', false);
    return false;
  }

  /**
   * Mark that feedback modal was shown
   * @param {object} posthogClient - Optional PostHog client
   */
  markFeedbackShown(posthogClient = null) {
    const tracker = this.getTracker();
    const now = new Date();

    tracker.lastShownDate = this.getDateString(now);
    tracker.lastShownTimestamp = Date.now();
    tracker.totalShown++;

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tracker));

    // Track with PostHog
    if (posthogClient && posthogClient.captureEvent) {
      posthogClient.captureEvent('feedback_modal_shown', {
        total_shown: tracker.totalShown,
        session_events: this.getSessionData().eventsReviewed
      });
    }
  }

  /**
   * Mark that feedback was submitted
   * @param {object} posthogClient - Optional PostHog client
   * @param {object} feedbackData - The submitted feedback data
   */
  markFeedbackSubmitted(posthogClient = null, feedbackData = {}) {
    const tracker = this.getTracker();
    tracker.totalSubmitted = (tracker.totalSubmitted || 0) + 1;
    tracker.lastSubmittedDate = this.getDateString(new Date());
    tracker.lastSubmittedTimestamp = Date.now();

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tracker));

    // Track with PostHog
    if (posthogClient && posthogClient.captureEvent) {
      posthogClient.captureEvent('feedback_submitted', {
        total_submitted: tracker.totalSubmitted,
        ...feedbackData
      });
    }
  }

  /**
   * Mark that feedback was skipped
   * @param {object} posthogClient - Optional PostHog client
   */
  markFeedbackSkipped(posthogClient = null) {
    const tracker = this.getTracker();
    tracker.totalSkipped = (tracker.totalSkipped || 0) + 1;

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tracker));

    // Track with PostHog
    if (posthogClient && posthogClient.captureEvent) {
      posthogClient.captureEvent('feedback_skipped', {
        total_skipped: tracker.totalSkipped
      });
    }
  }

  /**
   * Track event review (called when user views an event)
   * @param {object} posthogClient - Optional PostHog client
   */
  trackEventReview(posthogClient = null) {
    const sessionData = this.getSessionData();
    sessionData.eventsReviewed++;
    sessionData.lastActivityTime = Date.now();

    localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));

    // Track with PostHog (sample rate to avoid too many events)
    if (posthogClient && posthogClient.captureEvent && sessionData.eventsReviewed % 10 === 0) {
      posthogClient.captureEvent('session_milestone', {
        events_reviewed: sessionData.eventsReviewed,
        session_duration_minutes: this.getSessionDurationMinutes()
      });
    }
  }

  /**
   * Get session data for display/analytics
   * @returns {object}
   */
  getSessionDataForFeedback() {
    const sessionData = this.getSessionData();
    const now = new Date();
    const hour = now.getHours();

    let timeOfDay = 'afternoon';
    if (hour < 12) timeOfDay = 'morning';
    else if (hour < 17) timeOfDay = 'afternoon';
    else timeOfDay = 'evening';

    return {
      eventsReviewedCount: sessionData.eventsReviewed,
      sessionDurationMinutes: this.getSessionDurationMinutes(),
      timeOfDay
    };
  }

  /**
   * Get session duration in minutes
   * @returns {number}
   */
  getSessionDurationMinutes() {
    const sessionData = this.getSessionData();
    if (!sessionData.sessionStart) return 0;

    const durationMs = Date.now() - sessionData.sessionStart;
    return Math.floor(durationMs / (1000 * 60));
  }

  /**
   * Get session data from localStorage
   * @returns {object}
   */
  getSessionData() {
    const stored = localStorage.getItem(this.SESSION_KEY);
    if (stored) {
      const data = JSON.parse(stored);

      // Check if session is stale (> 12 hours old) and reset if so
      const sessionAge = Date.now() - data.sessionStart;
      const twelveHours = 12 * 60 * 60 * 1000;
      if (sessionAge > twelveHours) {
        return this.resetSession();
      }

      return data;
    }

    return this.resetSession();
  }

  /**
   * Reset session data
   * @returns {object}
   */
  resetSession() {
    const newSession = {
      sessionStart: Date.now(),
      eventsReviewed: 0,
      lastActivityTime: Date.now()
    };
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(newSession));
    return newSession;
  }

  /**
   * Get feedback tracker from localStorage
   * @returns {object}
   */
  getTracker() {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }

    return {
      lastShownDate: null,
      lastShownTimestamp: null,
      lastSubmittedDate: null,
      lastSubmittedTimestamp: null,
      totalShown: 0,
      totalSubmitted: 0,
      totalSkipped: 0
    };
  }

  /**
   * Get date string in YYYY-MM-DD format
   * @param {Date} date
   * @returns {string}
   */
  getDateString(date) {
    return date.toISOString().split('T')[0];
  }

  /**
   * Track feedback decision with PostHog
   * @private
   */
  trackDecision(posthogClient, reason, shouldShow, extraData = {}) {
    if (posthogClient && posthogClient.captureEvent) {
      posthogClient.captureEvent('feedback_trigger_decision', {
        reason,
        should_show: shouldShow,
        ...extraData
      });
    }
  }

  /**
   * Get statistics for debugging/admin panel
   * @returns {object}
   */
  getStats() {
    const tracker = this.getTracker();
    const sessionData = this.getSessionData();

    return {
      tracker,
      session: {
        ...sessionData,
        durationMinutes: this.getSessionDurationMinutes()
      },
      config: this.config
    };
  }

  /**
   * Reset all feedback tracking (admin function)
   */
  reset() {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.SESSION_KEY);
  }
}

// Export singleton instance
export default new FeedbackTriggerService();
